import React from 'react';

import { Dropdown } from 'antd-v5';
import { FormattedMessage } from 'react-intl';

import type { CounselorMeritBadge } from '@appTypes/esb';
import { Button, Checkbox, T } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './MBCYouthsMembersList.less';

interface MeritBadgeDropdownProps {
  counselingBadges: CounselorMeritBadge[];
  selectedMeritBadges: Set<number>;
  setSelectedMeritBadges: (value: Set<number>) => void;
  onChange?: (selectedIds: number[]) => void;
}

export const MeritBadgeDropdown: React.FC<MeritBadgeDropdownProps> = ({
  counselingBadges,
  selectedMeritBadges,
  setSelectedMeritBadges,
  onChange,
}) => {
  // Handle single item checkbox change
  const handleCheckboxChange = (id: number, checked: boolean) => {
    const nextIds = checked
      ? [...selectedMeritBadges, id]
      : [...selectedMeritBadges].filter(selectedId => selectedId !== id);

    setSelectedMeritBadges(new Set(nextIds));
    if (onChange) onChange(nextIds);
  };

  const handleSelectAllChange = (checked: boolean) => {
    const nextIds = checked
      ? counselingBadges.map(badge => badge.meritBadgeId)
      : [];
    setSelectedMeritBadges(new Set(nextIds));
    if (onChange) onChange(nextIds);
  };

  const handleClearAllChange = () => {
    setSelectedMeritBadges(new Set([]));
    if (onChange) onChange([]);
  };

  const dropdownRender = () => (
    <div className={styles.dropdownContainer}>
      <Button
        onClick={() => handleSelectAllChange(true)}
        size="small"
        uppercase={false}
        noBorder
        ghost
      >
        <FormattedMessage id="messaging.MeritBadgeDropdown.selectAll" />
      </Button>
      <Button
        onClick={() => handleClearAllChange()}
        size="small"
        uppercase={false}
        noBorder
        ghost
      >
        <FormattedMessage id="messaging.MeritBadgeDropdown.clearAll" />
      </Button>
      <div className={styles.dropdownMenu}>
        {counselingBadges.map(badge => {
          const isChecked = selectedMeritBadges.has(badge.meritBadgeId);
          return (
            <div key={badge.meritBadgeId} className={styles.dropdownItem}>
              <Checkbox
                checked={isChecked}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleCheckboxChange(badge.meritBadgeId, e.target.checked)
                }
                className={styles.checkboxFullWidth}
              >
                {badge.meritBadgeName}
              </Checkbox>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={true}
      getPopupContainer={triggerNode =>
        triggerNode.parentElement || document.body
      }
      dropdownRender={dropdownRender}
      placement="bottomRight"
      overlayClassName={styles.dropdownOverlay}
    >
      <T serif size="6" className={styles.link}>
        <FormattedMessage
          id="messaging.ManageMembersModal.filter"
          values={{
            selectedFilter: intl.formatMessage({
              id: 'messaging.MeritBadgeDropdown.button',
            }),
          }}
        />
      </T>
    </Dropdown>
  );
};
